import { useCallback, useMemo } from 'react';
import {
  ActionItemType,
  CreateActionItemDto,
  PriorityLevel,
  SpendAreaStage,
  getBackendTimestamp,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useValidationSchema, ValidationRule } from '@sib/ui/hooks';
import { isAxiosError } from 'axios';
import { addYears, subMonths } from 'date-fns';
import { z } from 'zod';

export enum ActionItemField {
  Title = 'title',
  Description = 'description',
  PriorityLevel = 'priorityLevel',
  Stage = 'stage',
  ActionType = 'actionType',
  MeetingLink = 'meetingLink',
  MeetingDate = 'meetingDate',
  LinkUrl = 'linkUrl',
  Organization = 'organizationId',
  SpendArea = 'spendArea',
  FileUpload = 'file',
  Deadline = 'deadline',
  Assignee = 'assignee',
  NotifyExternalClient = 'notifyExternalClient',
}

export const ACTION_ITEM_FORM_MEETING_TOP_LABEL_DATE_FORMAT = 'ppp';
export const ACTION_ITEM_FORM_CALENDAR_START_MONTH = subMonths(new Date(), 3);
export const ACTION_ITEM_FORM_CALENDAR_END_MONTH = addYears(new Date(), 5);

export const useActionItemFormValidationSchema = () => {
  const { rules } = useValidationSchema();

  return useMemo(
    () =>
      z
        .object({
          [ActionItemField.Title]: rules[ValidationRule.ActionTitle],
          [ActionItemField.Description]:
            rules[ValidationRule.ActionDescription].optional(),
          [ActionItemField.PriorityLevel]: rules[ValidationRule.PriorityLevel],
          [ActionItemField.Stage]: rules[ValidationRule.Stage],
          [ActionItemField.ActionType]:
            rules[ValidationRule.ActionType].nullable(),
          [ActionItemField.FileUpload]: z.any().optional(),
          [ActionItemField.MeetingLink]: z.string().optional(),
          [ActionItemField.LinkUrl]: z.string().optional(),
          [ActionItemField.MeetingDate]:
            rules[ValidationRule.ProperDate].optional(),
          [ActionItemField.Deadline]:
            rules[ValidationRule.ProperDate].optional(),
          [ActionItemField.Assignee]: z.string().nullable().optional(),
          [ActionItemField.Organization]: z.string(),
          [ActionItemField.SpendArea]: z.coerce.string().nullable().optional(),
          [ActionItemField.NotifyExternalClient]: z.boolean().optional(),
        })
        .refine(data => {
          if (data[ActionItemField.ActionType] === ActionItemType.MeetingLink) {
            return (
              data[ActionItemField.MeetingLink] &&
              data[ActionItemField.MeetingDate]
            );
          }

          if (data[ActionItemField.ActionType] === ActionItemType.FileUpload)
            return !!data[ActionItemField.FileUpload];

          if (data[ActionItemField.ActionType] === ActionItemType.Link)
            return !!data[ActionItemField.LinkUrl];

          return true;
        }),
    [rules]
  );
};

export type ActionItemFormValues = z.infer<
  ReturnType<typeof useActionItemFormValidationSchema>
>;

export const useActionTypeOptions = () => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        label: t('none'),
        value: null,
      },
      {
        label: t('enums.ActionType.fileUpload'),
        value: ActionItemType.FileUpload,
      },
      {
        label: t('enums.ActionType.meeting'),
        value: ActionItemType.MeetingLink,
      },
      {
        label: t('enums.ActionType.link'),
        value: ActionItemType.Link,
      },
    ],
    [t]
  );
};

export const useSpendAreaStageOptions = () => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      Object.values(SpendAreaStage).map(value => ({
        label: t(`enums.SpendAreaStage.${value}`),
        value: value,
      })),
    [t]
  );
};

export const getActionItemCreateDefaultValues = (
  organizationId: string
): ActionItemFormValues => ({
  [ActionItemField.Title]: '',
  [ActionItemField.Description]: '',
  [ActionItemField.PriorityLevel]: PriorityLevel.Low,
  [ActionItemField.Stage]: SpendAreaStage.MONITORING_SAVINGS,
  [ActionItemField.ActionType]: null,
  [ActionItemField.MeetingLink]: '',
  [ActionItemField.LinkUrl]: '',
  [ActionItemField.FileUpload]: null,
  [ActionItemField.MeetingDate]: new Date(),
  [ActionItemField.Deadline]: new Date(),
  [ActionItemField.Assignee]: null,
  [ActionItemField.Organization]: organizationId,
  [ActionItemField.SpendArea]: null,
  [ActionItemField.NotifyExternalClient]: false,
});

export const mapActionItemFormValuesToCreatePayload = (
  values: ActionItemFormValues,
  options?: { skipAssignee?: boolean }
): CreateActionItemDto => {
  const action =
    values.actionType === ActionItemType.MeetingLink
      ? {
          type: ActionItemType.MeetingLink,
          url: values.meetingLink ?? '',
          date: values.meetingDate?.toString() ?? new Date().toString(),
        }
      : values.actionType === ActionItemType.FileUpload
        ? {
            type: ActionItemType.FileUpload,
          }
        : values.actionType === ActionItemType.Link
          ? {
              type: ActionItemType.Link,
              url: values.linkUrl ?? '',
            }
          : null;

  const payload: CreateActionItemDto = {
    title: values.title,
    description: values.description ?? '',
    priorityLevel: values.priorityLevel,
    spendAreaStage: values.stage,
    notifyExternalClient: values.notifyExternalClient,
    deadline:
      values.actionType === ActionItemType.MeetingLink
        ? values.meetingDate
          ? getBackendTimestamp(values.meetingDate)
          : null
        : values.deadline
          ? getBackendTimestamp(values.deadline)
          : null,
    action,
    ...(options?.skipAssignee ? {} : { assignee: values.assignee }),
  };

  if (values.actionType === ActionItemType.FileUpload && values.file) {
    payload.file = values.file.file || values.file;
  }

  return payload;
};

export const useActionItemMutationErrorMessage = () => {
  const { t } = useTranslation();

  return useCallback(
    (err: unknown) => {
      if (isAxiosError(err)) {
        const message = err.response?.data.message;

        if (
          typeof message === 'string' &&
          message ===
            'File with that name and size within given organization already exists'
        ) {
          return {
            type: 'error' as const,
            message: t('errors.client.fileAlreadyUploaded'),
          };
        }
      }

      return null;
    },
    [t]
  );
};
